'use client';

import z from 'zod';
import { useValRoute } from '../../../val/val.client';
import { notFound, useRouter } from 'next/navigation';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import classNames from 'classnames';
import Button from '../../../components/Button/Button';
import Layout from '../../../containers/Layout';
import { RadioInputType, RadioTile } from '../../../components/RadioTile';
import pageVal from './page.val';

const positions: RadioInputType[] = [
  { id: 'tech', value: 'Teknolog' },
  { id: 'design', value: 'Designer' },
];

const Kaffeprat = ({ params }: { params: Promise<never> }) => {
  const page = useValRoute(pageVal, params);
  const router = useRouter();

  const kaffepratSchema = z.object({
    email: z.email(page?.form?.missingEmailError ?? ''),
    position: z.enum(page?.form?.positions ?? []),
    background: z.string().min(1, page?.form?.missingBackgroundError ?? ''),
    message: z.string().min(1, page?.form?.missingMessageError ?? ''),
  });

  type FormData = z.infer<typeof kaffepratSchema>;

  const formMethods = useForm<FormData>({
    resolver: zodResolver(kaffepratSchema),
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = formMethods;

  const onSubmit = async (data: FormData) => {
    const res = await fetch('/api/contact', {
      method: 'POST',
      headers: {
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!page) {
      console.error('Page data is missing');
      return;
    }
    if (res.ok) {
      router.push(page.form.successRoute);
      reset();
      return;
    }

    router.push(page.form.errorRoute);
  };

  const inputStyle =
    'w-full field-sizing-content border border-blank-black text-lg p-8 focus:outline-none focus:border focus:border-blank-yellow';

  if (!page) {
    return notFound();
  }
  const { title, placeholders } = page;

  return (
    <Layout>
      <FormProvider {...formMethods}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-[32px] w-full max-w-[900px]"
        >
          <h1 className="font-respira text-[3.5rem] desktop:text-[4rem] text-pretty">
            {title}
          </h1>
          <div className="flex flex-col gap-8 w-full">
            <label htmlFor="email" className="text-base">
              E-post
            </label>
            <input
              id="email"
              type="text"
              placeholder={placeholders.email}
              {...register('email')}
              className={classNames(
                inputStyle,
                errors.email
                  ? 'border-b-danger-primary-dark'
                  : 'border-b-blank-yellow'
              )}
            />
            {errors.email && (
              <p className="text-danger-primary-dark">{errors.email.message}</p>
            )}
          </div>

          <div className="flex-1 space-y-8">
            <div className="flex flex-row gap-24 w-full">
              {positions.map((position) => (
                <RadioTile
                  key={position.id}
                  input={position}
                  fieldName="position"
                />
              ))}
            </div>
            {errors.position && (
              <p className="text-danger-primary-dark">{'Velg stilling.'}</p>
            )}
          </div>

          <div className="flex flex-col gap-8">
            <label className="text-base" htmlFor="background">
              Kort om din bakgrunn
            </label>
            <textarea
              id="background"
              placeholder={placeholders.background}
              {...register('background')}
              rows={2}
              className={classNames(
                inputStyle,
                errors.background
                  ? 'border-b-danger-primary-dark'
                  : 'border-b-blank-yellow'
              )}
            ></textarea>
            {errors.background && (
              <p className="text-danger-primary-dark">
                {errors.background.message}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-8">
            <label className="text-base" htmlFor="message">
              Det viktigste du kan få ut av praten er...
            </label>
            <textarea
              id="message"
              placeholder={placeholders.intention}
              {...register('message')}
              className={classNames(
                inputStyle,
                errors.message
                  ? 'border-b-danger-primary-dark'
                  : 'border-b-blank-yellow'
              )}
            ></textarea>
            {errors.message && (
              <p className="text-danger-primary-dark">
                {errors.message.message}
              </p>
            )}
          </div>

          <Button
            withArrow
            scheme="dark"
            isDisabled={isSubmitting}
            type="submit"
          >
            Send inn
          </Button>
        </form>
      </FormProvider>
    </Layout>
  );
};

export default Kaffeprat;
